import React, { useState, useContext } from 'react';
import { AuthContext } from '../../store/AuthContext';
import {
  userFeedbackService,
  FeedbackFormData,
  TechnicalContext,
} from '../../services/userFeedbackService';
import './FeedbackDialog.css';

interface FeedbackDialogProps {
  isOpen: boolean;
  onClose: () => void;
  errorId?: string;
  errorMessage?: string;
  technicalContext?: TechnicalContext;
  defaultCategory?: FeedbackFormData['category'];
}

const FeedbackDialog: React.FC<FeedbackDialogProps> = ({
  isOpen,
  onClose,
  errorId,
  errorMessage,
  technicalContext,
  defaultCategory = 'bug',
}) => {
  const { user, isAuthenticated } = useContext(AuthContext);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [formData, setFormData] = useState<FeedbackFormData>({
    category: defaultCategory,
    priority: 'medium',
    summary: '',
    description: '',
    stepsToReproduce: '',
    expectedBehavior: '',
    actualBehavior: '',
    email: user?.email || '',
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.summary.trim() || !formData.description.trim()) {
      setError('Please provide both a summary and description.');
      return;
    }

    if (!formData.email?.trim()) {
      setError('Please provide an email address for follow-up.');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const context: TechnicalContext = {
        sentryErrorId: errorId,
        userAgent: navigator.userAgent,
        url: window.location.href,
        timestamp: new Date().toISOString(),
        errorMessage,
        ...technicalContext,
      };

      await userFeedbackService.submitFeedback(formData, context);

      setSubmitted(true);
      // Auto-close after 2 seconds
      setTimeout(() => {
        onClose();
        setSubmitted(false);
      }, 2000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit feedback');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
      setSubmitted(false);
      setError(null);
      // Reset form
      setFormData({
        category: defaultCategory,
        priority: 'medium',
        summary: '',
        description: '',
        stepsToReproduce: '',
        expectedBehavior: '',
        actualBehavior: '',
        email: user?.email || '',
      });
    }
  };

  if (!isOpen) return null;

  if (submitted) {
    return (
      <div className="feedback-dialog-overlay">
        <div className="feedback-dialog">
          <div className="feedback-success">
            <h3>✅ Feedback Submitted</h3>
            <p>Thank you for your feedback! We'll review it and get back to you if needed.</p>
            {errorId && (
              <p className="error-id">
                Reference ID: <code>{errorId}</code>
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="feedback-dialog-overlay" onClick={handleClose}>
      <div className="feedback-dialog" onClick={(e) => e.stopPropagation()}>
        <div className="feedback-header">
          <h3>{errorId ? '🐛 Report Issue' : '💬 Send Feedback'}</h3>
          <button
            type="button"
            onClick={handleClose}
            className="close-button"
            disabled={isSubmitting}
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="feedback-form">
          {errorId && (
            <div className="error-context">
              <p>We detected an error and would like your help understanding what happened.</p>
              {errorMessage && (
                <details>
                  <summary>Technical details</summary>
                  <code>{errorMessage}</code>
                </details>
              )}
            </div>
          )}

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="category">Category:</label>
              <select
                id="category"
                value={formData.category}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    category: e.target.value as FeedbackFormData['category'],
                  }))
                }
                disabled={isSubmitting}
              >
                <option value="bug">🐛 Bug Report</option>
                <option value="feature">💡 Feature Request</option>
                <option value="performance">⚡ Performance Issue</option>
                <option value="other">💬 Other</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="priority">Priority:</label>
              <select
                id="priority"
                value={formData.priority}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    priority: e.target.value as FeedbackFormData['priority'],
                  }))
                }
                disabled={isSubmitting}
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
              </select>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="email">Email for follow-up:</label>
            <input
              type="email"
              id="email"
              value={formData.email}
              onChange={(e) => setFormData((prev) => ({ ...prev, email: e.target.value }))}
              disabled={isSubmitting || isAuthenticated}
              placeholder="your.email@example.com"
              required
            />
            {isAuthenticated && <small>Using your account email</small>}
          </div>

          <div className="form-group">
            <label htmlFor="summary">Summary: *</label>
            <input
              type="text"
              id="summary"
              value={formData.summary}
              onChange={(e) => setFormData((prev) => ({ ...prev, summary: e.target.value }))}
              disabled={isSubmitting}
              placeholder="Brief description of the issue or feedback"
              maxLength={100}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="description">Description: *</label>
            <textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
              disabled={isSubmitting}
              placeholder="Please provide details about what happened or what you'd like to see improved"
              rows={4}
              required
            />
          </div>

          {formData.category === 'bug' && (
            <>
              <div className="form-group">
                <label htmlFor="stepsToReproduce">Steps to reproduce:</label>
                <textarea
                  id="stepsToReproduce"
                  value={formData.stepsToReproduce}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, stepsToReproduce: e.target.value }))
                  }
                  disabled={isSubmitting}
                  placeholder="1. Go to...&#10;2. Click on...&#10;3. Then..."
                  rows={3}
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="expectedBehavior">Expected result:</label>
                  <input
                    type="text"
                    id="expectedBehavior"
                    value={formData.expectedBehavior}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, expectedBehavior: e.target.value }))
                    }
                    disabled={isSubmitting}
                    placeholder="What should have happened?"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="actualBehavior">Actual result:</label>
                  <input
                    type="text"
                    id="actualBehavior"
                    value={formData.actualBehavior}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, actualBehavior: e.target.value }))
                    }
                    disabled={isSubmitting}
                    placeholder="What actually happened?"
                  />
                </div>
              </div>
            </>
          )}

          {error && <div className="error-message">{error}</div>}

          <div className="form-actions">
            <button
              type="button"
              onClick={handleClose}
              disabled={isSubmitting}
              className="cancel-button"
            >
              Cancel
            </button>
            <button type="submit" disabled={isSubmitting} className="submit-button">
              {isSubmitting ? 'Submitting...' : 'Submit Feedback'}
            </button>
          </div>

          {errorId && (
            <div className="reference-id">
              Reference ID: <code>{errorId}</code>
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default FeedbackDialog;
